import { createContext, type ReactNode, useContext, useMemo } from 'react';
import {
  EndpointsContainer,
  InternalAccessManager,
  PublicAccessManager
} from './logic';

interface WidgetAPI {
  apiAccessor: EndpointsContainer;
  apiManager: InternalAccessManager;
}

const WidgetAPIContext = createContext<WidgetAPI | undefined>(undefined);

export function useWidgetAPI() {
  const contextValue = useContext(WidgetAPIContext);

  if (!contextValue) {
    throw new Error(
      `useWidgetAPIContext should be used within WidgetAPIContext`
    );
  }

  return contextValue;
}

interface WidgetAPIProviderProps {
  children?: ReactNode;
}

export function WidgetAPIProvider({ children }: WidgetAPIProviderProps) {
  const api = useMemo(() => {
    const apiContainer = new EndpointsContainer();
    const internalAccessManager = new InternalAccessManager(apiContainer);
    const publicAccessManager = new Proxy(
      apiContainer,
      new PublicAccessManager()
    );

    return {
      apiAccessor: publicAccessManager,
      apiManager: internalAccessManager
    };
  }, []);

  return (
    <WidgetAPIContext.Provider value={api}>
      {children}
    </WidgetAPIContext.Provider>
  );
}
